import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Tag,
  Typography,
  message
} from "antd";
import { DownloadOutlined, FileTextOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { errorText, openBlob } from "../api";
import { formatCents, formatDate } from "../shared/money";
import Amount from "../shared/Amount";
import ResponsiveTable from "../../../../design-system/ResponsiveTable";
import { useIsMobile } from "../../../../design-system/useMediaQuery";
import { BATCH_STATUS_COLORS } from "./constants";
import {
  batchXml,
  cancelBatch,
  createBatch,
  createDirectDebit,
  debitCandidates,
  listBatches,
  listProposals,
  markUploaded
} from "./api";

const rowKey = r => `${r.kind}:${r.ref}`;

/** Zahlungsvorschlag → SEPA credit transfer, batch list, direct debits. */
const PaymentsTab = ({ accounts, canWrite, onChanged }) => {
  const { t } = useTranslation("accounting");
  const isMobile = useIsMobile();
  const [proposals, setProposals] = useState(null);
  const [batches, setBatches] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [useSkonto, setUseSkonto] = useState({});
  const [bankAccountId, setBankAccountId] = useState("");
  const [executionDate, setExecutionDate] = useState(dayjs().add(1, "day"));
  const [busy, setBusy] = useState(false);
  const [ddSelected, setDdSelected] = useState([]);
  const [ddDate, setDdDate] = useState(dayjs().add(3, "day"));

  const load = async () => {
    setLoading(true);
    try {
      const [p, b, c] = await Promise.all([listProposals(), listBatches(), debitCandidates()]);
      setProposals(p.data);
      setBatches(b.data.batches);
      setCandidates(c.data.invoices);
      const skontoDefaults = {};
      for (const item of p.data.belege) if (item.skonto) skontoDefaults[rowKey(item)] = true;
      setUseSkonto(s => ({ ...skontoDefaults, ...s }));
    } catch (err) {
      message.error(errorText(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!bankAccountId && accounts.length)
      setBankAccountId(
        accounts.find(a => a.active && a.kind === "current")?._id || accounts[0]._id
      );
  }, [accounts, bankAccountId]);

  const rows = proposals ? [...proposals.belege, ...proposals.filings, ...proposals.payroll] : [];
  const amountOf = r =>
    (r.kind === "beleg" && r.skonto && useSkonto[rowKey(r)]
      ? r.skonto.amountWithSkonto
      : r.amount) + (r.bauabzugsteuer ? r.bauabzugsteuer.withheld : 0);
  const selectedRows = rows.filter(r => selected.includes(rowKey(r)));
  const total = selectedRows.reduce((s, r) => s + amountOf(r), 0);
  const saving = selectedRows.reduce(
    (s, r) => s + (r.kind === "beleg" && r.skonto && useSkonto[rowKey(r)] ? r.skonto.saving : 0),
    0
  );

  const create = async () => {
    if (!bankAccountId) return message.warning(t("Choose a bank account"));
    setBusy(true);
    try {
      const res = await createBatch({
        bankAccountId,
        executionDate: executionDate.format("YYYY-MM-DD"),
        items: selectedRows.map(r => ({
          kind: r.kind,
          ref: r.ref,
          useSkonto: Boolean(r.skonto && useSkonto[rowKey(r)])
        }))
      });
      message.success(t("SEPA file created"));
      await openBlob(batchXml(res.data.batch._id), `pain001-${res.data.batch.executionDate}.xml`);
      setSelected([]);
      load();
      onChanged?.();
    } catch (err) {
      message.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const createDd = async () => {
    if (!bankAccountId) return message.warning(t("Choose a bank account"));
    setBusy(true);
    try {
      const res = await createDirectDebit({
        bankAccountId,
        collectionDate: ddDate.format("YYYY-MM-DD"),
        invoiceIds: ddSelected
      });
      message.success(
        t("{{count}} direct debit file(s) created", { count: res.data.batches.length })
      );
      for (const b of res.data.batches)
        await openBlob(batchXml(b._id), `pain008-${b.executionDate}-${b.messageId}.xml`);
      setDdSelected([]);
      load();
    } catch (err) {
      message.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    {
      title: t("Due"),
      dataIndex: "dueDate",
      width: 110,
      render: (v, r) => (
        <Space size={4} wrap>
          <span className="acct-num">{formatDate(v)}</span>
          {r.overdue ? <Tag color="red">{t("overdue")}</Tag> : null}
        </Space>
      )
    },
    {
      title: t("Recipient"),
      dataIndex: "name",
      mobile: "title",
      render: (v, r) => (
        <div className="acct-wrap">
          <div>{v}</div>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {r.kind === "beleg"
              ? r.invoiceNumber || r.belegNumber
              : r.kind === "filing"
                ? `${r.type} ${r.period}`
                : r.kind === "payroll"
                  ? `${r.employees.length} ${t("employees")}`
                  : ""}
            {r.iban ? ` · ${r.iban}` : r.kind !== "payroll" ? ` · ${t("IBAN missing")}` : ""}
            {r.missingIban?.length ? ` · ${t("IBAN missing")}: ${r.missingIban.join(", ")}` : ""}
          </Typography.Text>
        </div>
      )
    },
    {
      title: t("Amount"),
      dataIndex: "amount",
      align: "end",
      width: 130,
      render: v => <Amount cents={v} />
    },
    {
      title: t("Skonto"),
      key: "skonto",
      width: 220,
      render: (_, r) =>
        r.kind === "beleg" && r.skonto ? (
          <Space wrap>
            <Switch
              size="small"
              checked={Boolean(useSkonto[rowKey(r)])}
              onChange={v => setUseSkonto(s => ({ ...s, [rowKey(r)]: v }))}
              disabled={!canWrite}
            />
            <span>
              {r.skonto.percent} % {t("until")} {formatDate(r.skonto.until)}
            </span>
            <Typography.Text type="success">
              <span className="acct-num">−{formatCents(r.skonto.saving)}</span>
            </Typography.Text>
          </Space>
        ) : r.bauabzugsteuer ? (
          <Typography.Text type="secondary">
            {t("Bauabzugsteuer")}{" "}
            <span className="acct-num">{formatCents(r.bauabzugsteuer.withheld)}</span> →{" "}
            {t("Finanzamt")} {r.bauabzugsteuer.finanzamt}
          </Typography.Text>
        ) : null
    },
    {
      title: t("To pay"),
      key: "pay",
      align: "end",
      width: 130,
      mobile: "subtitle",
      render: (_, r) => <Amount cents={amountOf(r)} strong large={isMobile} />
    },
    {
      title: "",
      key: "scheduled",
      mobile: "subtitle",
      width: 120,
      render: (_, r) => (r.scheduled ? <Tag color="blue">{t("scheduled")}</Tag> : null)
    }
  ];

  const batchColumns = [
    {
      title: t("Created"),
      dataIndex: "createdAt",
      width: 130,
      render: v => <span className="acct-num">{new Date(v).toLocaleString("de-DE")}</span>
    },
    {
      title: t("Type"),
      dataIndex: "type",
      width: 130,
      mobile: "title",
      render: v => (v === "direct_debit" ? t("Direct debit") : t("Credit transfer"))
    },
    {
      title: t("Execution date"),
      dataIndex: "executionDate",
      width: 120,
      render: v => <span className="acct-num">{formatDate(v)}</span>
    },
    { title: t("Bank account"), dataIndex: ["bankAccount", "name"], ellipsis: true },
    {
      title: t("Items"),
      dataIndex: "items",
      width: 70,
      align: "end",
      render: v => <span className="acct-num">{v.length}</span>
    },
    {
      title: t("Amount"),
      dataIndex: "totalAmount",
      align: "end",
      width: 130,
      mobile: "subtitle",
      render: v => <Amount cents={v} strong={isMobile} large={isMobile} />
    },
    {
      title: t("Status"),
      dataIndex: "status",
      width: 110,
      render: v => <Tag color={BATCH_STATUS_COLORS[v]}>{t(v)}</Tag>
    },
    {
      title: "",
      key: "actions",
      width: 260,
      render: (_, b) => (
        <Space size={4} wrap>
          <Button
            size="small"
            icon={<DownloadOutlined />}
            onClick={() =>
              openBlob(
                batchXml(b._id),
                `${b.type === "direct_debit" ? "pain008" : "pain001"}-${b.executionDate}.xml`
              )
            }
          >
            XML
          </Button>
          {canWrite && b.status === "generated" ? (
            <Button
              size="small"
              onClick={() =>
                markUploaded(b._id)
                  .then(load)
                  .catch(err => message.error(errorText(err)))
              }
            >
              {t("Mark as uploaded")}
            </Button>
          ) : null}
          {canWrite && ["generated", "uploaded"].includes(b.status) ? (
            <Button
              size="small"
              danger
              onClick={() =>
                Modal.confirm({
                  title: t("Cancel this batch?"),
                  okText: t("Yes"),
                  cancelText: t("No"),
                  onOk: () =>
                    cancelBatch(b._id)
                      .then(load)
                      .catch(err => message.error(errorText(err)))
                })
              }
            >
              {t("Cancel")}
            </Button>
          ) : null}
        </Space>
      )
    }
  ];

  const ddColumns = [
    { title: t("Invoice"), dataIndex: "invoiceNumber", width: 140 },
    {
      title: t("Customer"),
      dataIndex: "customer",
      mobile: "title",
      render: v => <span className="acct-wrap">{v}</span>
    },
    {
      title: t("Mandate"),
      dataIndex: ["mandate", "reference"],
      render: (v, r) => `${v} (${r.mandate.sequence})`
    },
    {
      title: t("Due"),
      dataIndex: "dueDate",
      width: 110,
      render: v => <span className="acct-num">{formatDate(v ? String(v).slice(0, 10) : "")}</span>
    },
    {
      title: t("open"),
      dataIndex: "open",
      align: "end",
      width: 120,
      mobile: "subtitle",
      render: v => <Amount cents={v} strong={isMobile} large={isMobile} />
    },
    {
      title: "",
      key: "s",
      mobile: "subtitle",
      width: 100,
      render: (_, r) => (r.scheduled ? <Tag color="blue">{t("scheduled")}</Tag> : null)
    }
  ];

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="large">
      <Card title={t("Payment proposal")} loading={loading}>
        {proposals &&
        !proposals.taxOfficeIban &&
        (proposals.filings.length || proposals.belege.some(b => b.bauabzugsteuer)) ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message={t(
              "The Finanzamt IBAN is missing in the accounting settings; tax payments cannot be added to a file."
            )}
          />
        ) : null}
        <ResponsiveTable
          rowKey={rowKey}
          size="small"
          columns={columns}
          dataSource={rows}
          pagination={false}
          rowSelection={
            canWrite
              ? {
                  selectedRowKeys: selected,
                  onChange: keys => setSelected(keys),
                  getCheckboxProps: r => ({
                    disabled:
                      Boolean(r.scheduled) ||
                      (r.kind !== "payroll" && !r.iban) ||
                      (r.missingIban && r.missingIban.length > 0)
                  })
                }
              : undefined
          }
        />
        {canWrite ? (
          <Row gutter={[12, 12]} align="middle" style={{ marginTop: 16 }}>
            <Col xs={24} md={8}>
              <Select
                style={{ width: "100%" }}
                value={bankAccountId || undefined}
                onChange={setBankAccountId}
                placeholder={t("Bank account")}
                options={accounts
                  .filter(a => a.active)
                  .map(a => ({ value: a._id, label: `${a.name} (${a.iban})` }))}
              />
            </Col>
            <Col xs={24} md={6}>
              <DatePicker
                style={{ width: "100%" }}
                value={executionDate}
                onChange={v => v && setExecutionDate(v)}
                format="DD.MM.YYYY"
              />
            </Col>
            <Col xs={24} md={10}>
              <div className="acct-actions" style={{ alignItems: "center" }}>
                <Typography.Text strong>
                  {t("Total selected")}: <Amount cents={total} strong />
                </Typography.Text>
                {saving ? (
                  <Typography.Text type="success">
                    ({t("Skonto saving")} <span className="acct-num">{formatCents(saving)}</span>)
                  </Typography.Text>
                ) : null}
                <Button
                  type="primary"
                  icon={<FileTextOutlined />}
                  disabled={!selected.length}
                  loading={busy}
                  onClick={create}
                >
                  {t("Create SEPA file")}
                </Button>
              </div>
            </Col>
          </Row>
        ) : null}
      </Card>

      <Card title={t("Payment batches")} loading={loading}>
        <ResponsiveTable
          rowKey="_id"
          size="small"
          columns={batchColumns}
          dataSource={batches}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 900 }}
        />
      </Card>

      <Card title={t("Direct debits")} loading={loading}>
        {proposals && !proposals.glaeubigerId ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message={t("A Gläubiger-ID is required for direct debits (accounting settings).")}
          />
        ) : null}
        <ResponsiveTable
          rowKey="_id"
          size="small"
          columns={ddColumns}
          dataSource={candidates}
          pagination={false}
          locale={{ emptyText: t("No open invoices of customers with a SEPA mandate") }}
          rowSelection={
            canWrite
              ? {
                  selectedRowKeys: ddSelected,
                  onChange: setDdSelected,
                  getCheckboxProps: r => ({ disabled: r.scheduled })
                }
              : undefined
          }
        />
        {canWrite ? (
          <>
            <Divider style={{ margin: "12px 0" }} />
            <Space wrap align="center">
              <DatePicker value={ddDate} onChange={v => v && setDdDate(v)} format="DD.MM.YYYY" />
              <Typography.Text type="secondary">{t("Collection date")}</Typography.Text>
              <Button
                type="primary"
                disabled={!ddSelected.length || !proposals?.glaeubigerId}
                loading={busy}
                onClick={createDd}
              >
                {t("Create direct debit file")}
              </Button>
            </Space>
          </>
        ) : null}
      </Card>
    </Space>
  );
};

export default PaymentsTab;
